import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  CdkDropListGroup,
} from '@angular/cdk/drag-drop';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { MatButton } from '@angular/material/button';
import {
  MatCardActions,
  MatCardContent,
  MatCardHeader,
  MatCardTitle,
} from '@angular/material/card';
import {
  MatCheckboxChange,
  MatCheckboxModule,
} from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { TranslatePipe } from '@ngx-translate/core';
import { UserSerialize } from '../../shared/models/user/user.serialize';
import { DialogService } from '../../shared/services/dialog.service';

interface DisplayedColumn {
  value: string;
  disabled: boolean;
  originList?: 'displayed' | 'default';
}

@Component({
  selector: 'dnv-drag-drop-file-upload',
  templateUrl: './users-drag-and-drop.component.html',
  imports: [
    MatCardHeader,
    MatCardTitle,
    MatCardContent,
    MatCardActions,
    MatIcon,
    MatButton,
    TranslatePipe,
    MatDividerModule,
    MatCheckboxModule,
    MatTableModule,
    CdkDrag,
    CdkDropList,
    CdkDropListGroup,
    MatProgressBar,
  ],
})
export class UsersDragAndDropComponent implements OnInit {
  private dialogService = inject(DialogService);
  hasHeader = signal(true);
  displayColumnMatch = signal(false);
  isImportingData = signal(false);
  importCompleted = signal(false);

  @Input() isDataImportVisible = signal<boolean>(false);
  @Input() isDataImportCompleted = signal<boolean>(false);
  @Input() isDataImportValid = signal<boolean>(false);

  @Output() importClicked = new EventEmitter<any>();

  file: File | null = null;
  fileContent: string = '';
  csvData: string[][] = [];
  dataSource = new MatTableDataSource<string[]>(this.csvData);
  displayedColumns: DisplayedColumn[] = [];
  importingColumns: DisplayedColumn[] = [
    { value: 'Qualifica', disabled: true, originList: 'default' },
    { value: 'Matricola', disabled: true, originList: 'default' },
    { value: 'Livello', disabled: true, originList: 'default' },
    { value: 'Cognome', disabled: true, originList: 'default' },
    { value: 'Nome', disabled: true, originList: 'default' },
    { value: 'Email', disabled: true, originList: 'default' },
    {
      value: 'Anzianita Pubblica Amministrazione',
      disabled: true,
      originList: 'default',
    },
    {
      value: 'Anzianita Categoria Attuale',
      disabled: true,
      originList: 'default',
    },
    {
      value: 'Anzianita Livello Attuale',
      disabled: true,
      originList: 'default',
    },
    {
      value: 'Data Assunzione in ENAC',
      disabled: true,
      originList: 'default',
    },
  ];
  defaultColumns: DisplayedColumn[] = [
    { value: 'Qualifica', disabled: true, originList: 'default' },
    { value: 'Matricola', disabled: true, originList: 'default' },
    { value: 'Livello', disabled: true, originList: 'default' },
    { value: 'Cognome', disabled: true, originList: 'default' },
    { value: 'Nome', disabled: true, originList: 'default' },
    { value: 'Email', disabled: true, originList: 'default' },
    {
      value: 'Anzianita Pubblica Amministrazione',
      disabled: true,
      originList: 'default',
    },
    {
      value: 'Anzianita Categoria Attuale',
      disabled: true,
      originList: 'default',
    },
    {
      value: 'Anzianita Livello Attuale',
      disabled: true,
      originList: 'default',
    },
    {
      value: 'Data Assunzione in ENAC',
      disabled: true,
      originList: 'default',
    },
  ];
  isDragging = false;

  ngOnInit(): void {
    this.updateDisplayedColumns();
  }

  private updateDisplayedColumns(): void {
    if (this.csvData.length == 1) {
      this.hasHeader.set(false);
    }
    if (this.csvData.length > 0) {
      if (this.hasHeader()) {
        this.displayedColumns = this.csvData[0].map((header) => ({
          value: header,
          disabled: false,
          originList: 'displayed',
        }));
        this.dataSource.data = this.csvData.slice(1);
      } else {
        this.displayedColumns = this.defaultColumns.map((column) => ({
          value: column.value,
          disabled: false,
          originList: 'displayed',
        }));
        this.dataSource.data = this.csvData;
      }
    }
  }

  onHasHeaderChange(event: MatCheckboxChange): void {
    this.hasHeader.set(event.checked);
    this.updateDisplayedColumns();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
    const droppedFiles = event.dataTransfer?.files;
    if (droppedFiles && droppedFiles.length > 0) {
      this.handleFile(droppedFiles[0]);
    }
  }

  onFileSelected(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const selectedFile = inputElement.files?.[0];
    if (selectedFile) {
      this.handleFile(selectedFile);
    }
  }

  handleFile(selectedFile: File): void {
    this.file = selectedFile;
    this.fileContent = '';
    this.csvData = [];
    this.dataSource.data = [];
    const reader = new FileReader();

    reader.onload = (e) => {
      this.fileContent = e.target?.result as string;
      this.parseCsv(this.fileContent);
      this.updateDisplayedColumns();
    };

    reader.onerror = (e) => {
      this.dialogService.showError('Error reading the file.');
      this.file = null;
      this.fileContent = '';
      this.csvData = [];
      this.dataSource.data = [];
    };

    reader.readAsText(selectedFile);
  }

  removeFile(): void {
    this.file = null;
    this.fileContent = '';
    this.csvData = [];
    this.dataSource.data = [];
  }

  back(): void {
    this.displayColumnMatch.set(false);
  }

  parseCsv(csvText: string): void {
    const lines = csvText
      .trim()
      .split('\n')
      .map((line) => line.trim());
    this.csvData = lines.map((line) =>
      line.split(',').map((cell) => cell.trim()),
    );
    this.updateDisplayedColumns();
  }

  getDisplayedColumnsValues() {
    return this.displayedColumns.map((col) => col.value);
  }

  dropToImport(event: CdkDragDrop<any>, importIndex: number) {
    if (event.previousContainer.id !== event.container.id) {
      this.importingColumns[importIndex] = {
        ...event.item.data,
      } as DisplayedColumn;
      this.importingColumns[importIndex].disabled = false;
      this.displayedColumns[event.previousIndex].disabled = true;
    }
  }

  returnToDisplayed(importingColumn: DisplayedColumn, indexToRemove: number) {
    this.importingColumns.splice(
      indexToRemove,
      1,
      this.defaultColumns[indexToRemove],
    );
    const index = this.displayedColumns.findIndex(
      (column) => column.value === importingColumn.value,
    );
    this.displayedColumns[index].disabled = false;
  }

  returnToDefault(itemToRemove: DisplayedColumn) {
    if (itemToRemove.originList === 'displayed') {
      this.displayedColumns = this.displayedColumns.map((item) => {
        if (item.value === itemToRemove.value) {
          item.disabled = false;
        }
        return item;
      });
    }

    let indexToReplace = 0;
    this.displayedColumns.map((item) => {
      if (item.value === itemToRemove.value) {
        this.importingColumns.filter((_, index) => {
          indexToReplace = this.importingColumns.findIndex(
            (item) => item.value === itemToRemove.value,
          );
        });
      }
    });

    this.importingColumns.splice(
      indexToReplace,
      1,
      this.defaultColumns[indexToReplace],
    );
  }

  isPairValid(): boolean {
    return this.importingColumns.some((column) => column.disabled);
  }

  pairColumns(): void {
    this.autoPairColumns();
    this.displayColumnMatch.set(true);
  }

  private autoPairColumns() {
    this.displayedColumns.forEach((displayedCol) => {
      const importingColumnIndex = this.importingColumns.findIndex(
        (importingCol) =>
          displayedCol.value.toLowerCase() === importingCol.value.toLowerCase(),
      );

      if (importingColumnIndex > -1) {
        this.importingColumns[importingColumnIndex].value = displayedCol.value;
        this.importingColumns[importingColumnIndex].disabled = false;

        displayedCol.disabled = true;
      }
    });
  }

  getUsers(): any {
    const dataToImport = this.dataSource.data;
    const headers = (
      this.hasHeader() ? this.importingColumns : this.defaultColumns
    ).map((column: DisplayedColumn | string) => {
      return typeof column === 'string' ? column : column.value;
    });

    const users: any[] = [];

    dataToImport.forEach((row) => {
      const userDetailsData: { [key: string]: any } = {};
      headers.forEach((header, index) => {
        userDetailsData[header] = row[index];
      });
      users.push(UserSerialize.toImportedUser(userDetailsData, headers));
    });

    return users;
  }

  onImportButtonClicked() {
    this.isImportingData.set(true);
    return this.importClicked.emit(this.getUsers());
  }

  toggle() {
    this.isDataImportVisible.set(false);
    this.isDataImportCompleted.set(false);
  }

  dataSourceIndices(): number[] {
    return Array.from(
      { length: Math.min(3, this.dataSource.data.length) },
      (_, i) => i,
    );
  }

  isCsvDataEmpty(): boolean {
    return (
      !this.csvData ||
      this.csvData.length === 0 ||
      this.csvData.every(
        (row: string[]) =>
          !row ||
          row.length === 0 ||
          row.every((cell) => !cell || cell.trim() === ''),
      )
    );
  }
}
